import { Box } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { LayoutType } from 'model/deals.model';
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { ViewTypeEnum } from 'service/businesses.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { BusinessType, fetchBusinesses } from 'store/slices/businessesSlice';
import AdminSearch from './AdminSearch';
import { Filters } from './Filters';
import Grid from './Grid';
import { muiStyles } from './muiStyles';
import { OptionMenu } from './OptionMenu';
import TableView from './Table';
import { getInitialSelectedBusinessState } from './utils';

const BusinessesMain = () => {
  const dispatch = useAppDispatch();
  const {
    tabValue,
    pagination,
    categories,
    categoryValue,
    searchTerm,
    sortBy,
    sortOrder,
    allBusinessesCount,
    myBusinessesCount,
  } = useAppSelector((state) => state.businesses);

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const [menuElement, setMenuElement] = useState<SVGSVGElement | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState(getInitialSelectedBusinessState());
  const { isFollowing, isTargeted, businessId, zoominfoCompanyId } = selectedBusiness;

  const tabs = [
    {
      label: `My Businesses (${myBusinessesCount})`,

      Component: <></>,
    },
    {
      label: `All Businesses (${allBusinessesCount})`,

      Component: <></>,
    },
  ];
  const handleCloseSettings = () => {
    setSelectedBusiness(getInitialSelectedBusinessState());
    setMenuElement(null);
  };

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, data: BusinessType) => {
    event.stopPropagation();
    setMenuElement(event.currentTarget);
    setSelectedBusiness(data);
  };

  const businessesViewType =
    (sessionStorage.getItem('businessesViewType') as LayoutType) || LayoutType.KANBAN;
  const [layout, setLayout] = useState<LayoutType>(businessesViewType);

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('businessesViewType', newValue);
    setLayout(newValue);
  };

  const VIEW_MAP = [ViewTypeEnum.mine, ViewTypeEnum.all] as const;

  const getBusinesses = useCallback(() => {
    const categoryName = categories?.[categoryValue]?.name;

    dispatch(
      fetchBusinesses({
        view: VIEW_MAP[tabValue] || ViewTypeEnum.mine,
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchTerm,
        category: categoryName,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    tabValue,
    dispatch,
    pagination.page,
    pagination.pageSize,
    searchTerm,
    categoryValue,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    getBusinesses();
  }, [getBusinesses]);

  const Content = layout === LayoutType.KANBAN ? Grid : TableView;
  const isMybusinessesTab = tabs[tabValue]?.label?.includes('My Businesses');
  const tabsWithId = tabs.map((tab, index) => ({
    ...tab,
    id: index,
    isHidden: tabValue !== index,
  }));
  return (
    <Box sx={muiStyles.root}>
      <Filters layout={layout} onLayoutChange={handleLayoutChange} tabs={tabsWithId} />
      <Content handleOpenSettings={handleOpenSettings} isMybusinessesTab={isMybusinessesTab} />
      <AdminSearch />

      <OptionMenu
        data={{ businessId, isFollowing, isTargeted, zoominfoCompanyId }}
        anchorElement={menuElement}
        onClose={handleCloseSettings}
        startOperation={startOperation}
        completeOperation={completeOperation}
        getBusinesses={getBusinesses}
        isMybusinessesTab={isMybusinessesTab}
      />

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default BusinessesMain;
